
import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface DropdownProps {
  label: string;
  options: {
    label: string;
    value: string;
    icon?: React.ReactNode;
  }[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const Dropdown = ({
  label,
  options,
  value,
  onChange,
  className,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={cn(
          "flex items-center justify-between w-full px-4 py-2 text-sm border rounded-md transition-all bg-background hover:bg-muted/50",
          className
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {selectedOption?.icon && <span>{selectedOption.icon}</span>}
          <span>{selectedOption?.label || label}</span>
        </div>
        <ChevronDown
          size={16}
          className={cn("transition-transform", isOpen && "transform rotate-180")}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg animate-fade-in">
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "flex items-center gap-2 w-full px-4 py-2 text-sm text-left transition-colors hover:bg-muted",
                  option.value === value && "bg-muted/70 font-medium"
                )}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                {option.icon && <span>{option.icon}</span>}
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
